import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { MvpPlanCreationModal, FormData as ModalFormData } from '@/components/mvp-plans/MvpPlanCreationModal';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { AiItemsList } from '@/components/shared/AiItemsList';
import { AiDetailPanel } from '@/components/shared/AiDetailPanel';
import { CrossSellCard } from '@/components/shared/CrossSellCard';
import { projectsApi, mvpPlansApi, positioningsApi, marketAnalysesApi, personasApi, businessModelsApi } from '@/utils/api-pb';
import { useAuth } from '@/utils/AuthContext';
import { pb } from '@/utils/pocketbase';
import type { Project, MvpPlan, Positioning, MarketAnalysis, Persona, BusinessModel } from '@/utils/pocketbase';
import { API_URL } from '@/utils/config';



import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
export default function MvpPlanPage() {
  const { t } = useTranslation('mvp-plan');
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isFree } = useAuth();

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [mvpPlans, setMvpPlans] = useState<MvpPlan[]>([]);
  const [positionings, setPositionings] = useState<Positioning[]>([]);
  const [marketAnalyses, setMarketAnalyses] = useState<MarketAnalysis[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [businessModels, setBusinessModels] = useState<BusinessModel[]>([]);
  const [selectedMvpPlan, setSelectedMvpPlan] = useState<MvpPlan | null>(null);

  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mvpPlanToDelete, setMvpPlanToDelete] = useState<string | null>(null);

  const [formData, setFormData] = useState<ModalFormData>({
    project_id: '',
    market_analysis_id: '',
    persona_id: '',
    positioning_id: '',
    business_model_id: '',
    budget: '',
    comment: ''
  });

  // Permissions - MVP ONLY for paid users
  const isPremium = !isFree;
  const canCreateNew = isPremium;
  const canEdit = isPremium;
  const canDelete = isPremium;

  // Tooltip state for create button
  const [tooltipVisible, setTooltipVisible] = useState<{ id: string, type: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-select MVP plan (prefer recordId from query param, fallback to first)
  useEffect(() => {
    if (mvpPlans.length > 0 && !selectedMvpPlan) {
      const targetId = searchParams.get('recordId');
      const target = targetId ? mvpPlans.find(p => p.id === targetId) : null;
      setSelectedMvpPlan(target ?? mvpPlans[0] ?? null);
    }
  }, [mvpPlans, selectedMvpPlan, searchParams]);

  const loadData = async () => {
    try {
      setIsInitialLoading(true);
      const [projectsResult, mvpPlansResult, positioningsResult, analysesResult, personasResult, businessModelsResult] = await Promise.all([
        projectsApi.list(),
        mvpPlansApi.list(),
        positioningsApi.list(),
        marketAnalysesApi.list(),
        personasApi.list(),
        businessModelsApi.list(),
      ]);
      setProjects(projectsResult);
      setMvpPlans(mvpPlansResult);
      setPositionings(positioningsResult);
      setMarketAnalyses(analysesResult);
      setPersonas(personasResult);
      setBusinessModels(businessModelsResult);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSubmitModal = async (modalFormData: ModalFormData) => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai/mvp-plans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${pb.authStore.token}`,
        },
        body: JSON.stringify({
          replaceId: isEditing ? selectedMvpPlan?.id : undefined,
          projectId: modalFormData.project_id,
          marketAnalysisId: modalFormData.market_analysis_id,
          personaId: modalFormData.persona_id,
          positioningId: modalFormData.positioning_id,
          businessModelId: modalFormData.business_model_id || undefined,
          budget: modalFormData.budget,
          comment: modalFormData.comment || undefined,
          lang,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || t('toast.createError'));
      }

      setShowNewModal(false);
      setShowEditModal(false);
      setIsEditing(false);
      setFormData({
        project_id: '',
        market_analysis_id: '',
        persona_id: '',
        positioning_id: '',
        business_model_id: '',
        budget: '',
        comment: ''
      });

      // Reload and select the new plan
      await loadData();
      if (data.mvpPlan) {
        setSelectedMvpPlan(data.mvpPlan);
      }
    } catch (error) {
      console.error('Error creating MVP plan:', error);
      const errorMessage = error instanceof Error ? error.message : t('toast.createError');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await mvpPlansApi.delete(id);
      setShowDeleteConfirm(false);
      setMvpPlanToDelete(null);
      if (selectedMvpPlan?.id === id) {
        setSelectedMvpPlan(null);
      }
      loadData();
    } catch (error) {
      console.error('Error deleting MVP plan:', error);
      toast.error(t('toast.deleteError'));
    }
  };

  const handleCreateNew = () => {
    if (!canCreateNew) return;

    setFormData({
      project_id: '',
      market_analysis_id: '',
      persona_id: '',
      positioning_id: '',
      business_model_id: '',
      budget: '',
      comment: ''
    });
    setIsEditing(false);
    setSelectedMvpPlan(null);
    setShowNewModal(true);
  };

  const handleEditMvpPlan = (mvpPlan: MvpPlan) => {
    if (!canEdit) return;

    setFormData({
      project_id: mvpPlan.project || '',
      market_analysis_id: mvpPlan.input_data?.marketAnalysisId || '',
      persona_id: mvpPlan.input_data?.personaId || '',
      positioning_id: mvpPlan.input_data?.positioningId || '',
      business_model_id: mvpPlan.input_data?.businessModelId || '',
      budget: mvpPlan.input_data?.budget || '',
      comment: mvpPlan.input_data?.comment || ''
    });
    setSelectedMvpPlan(mvpPlan);
    setIsEditing(true);
    setShowEditModal(true);
  };

  const handleSelectMvpPlan = (mvpPlan: MvpPlan) => {
    setSelectedMvpPlan(mvpPlan);
  };

  const handleCloseModal = () => {
    setShowNewModal(false);
    setShowEditModal(false);
    setIsEditing(false);
  };

  // Get MVP plan title based on product name
  const getMvpPlanTitle = (mvpPlan: MvpPlan): string => {
    const project = projects.find(p => p.id === mvpPlan.project);
    return t('itemTitle', { projectName: project?.name || t('defaultProjectName') });
  };

  // Count MVP plans per project for numbering (chronological: oldest = 1)
  const mvpPlanNumbers = useMemo(() => {
    const projectCounts: Record<string, number> = {};
    const numbers: Record<string, { displayNumber: number; total: number }> = {};

    mvpPlans.forEach(mvpPlan => {
      const projectId = mvpPlan.project || '';
      projectCounts[projectId] = (projectCounts[projectId] || 0) + 1;
    });

    // Data is already sorted by created (oldest first)
    const projectIndexes: Record<string, number> = {};
    mvpPlans.forEach(mvpPlan => {
      const projectId = mvpPlan.project || '';
      projectIndexes[projectId] = (projectIndexes[projectId] || 0) + 1;
      numbers[mvpPlan.id] = {
        displayNumber: projectIndexes[projectId] ?? 1,
        total: projectCounts[projectId] ?? 1
      };
    });

    return numbers;
  }, [mvpPlans]);

  const getMvpPlanListTitle = (mvpPlan: MvpPlan): string => {
    const numberInfo = mvpPlanNumbers[mvpPlan.id];
    const shouldShowNumber = numberInfo && numberInfo.total > 1;
    const displayNum = numberInfo?.displayNumber ?? 1;
    return `${getMvpPlanTitle(mvpPlan)}${shouldShowNumber ? ` ${displayNum}` : ''}`;
  };

  const getMvpPlanSubtitle = (mvpPlan: MvpPlan): string | null => {
    return new Date(mvpPlan.created).toLocaleDateString();
  };

  const pageDescription = t('description');

  // Free tier - show paywall
  if (!isInitialLoading && isFree) {
    return (
      <><PageHeader title={t('title')} description={pageDescription} />

          {/* Paywall Card */}
          <SubscriptionLimitBanner
            title={t('paidBanner.title')}
            description={t('paidBanner.description')}
            buttonText={t('paidBanner.button')}
          />
      </>
    );
  }

  return (
    <>{isInitialLoading ? (
          /* Initial Loading State */
          <>
            <PageHeader title={t('title')} description={pageDescription} />
            <div className="flex items-center justify-center py-[80px]">
              <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
            </div>
          </>
        ) : mvpPlans.length === 0 ? (
          /* Empty State */
          <><PageHeader title={t('title')} description={pageDescription} />
          <div className="flex flex-col gap-[24px]">
            <div className="font-segoe text-[16px] leading-[24px] text-[#565858] max-w-[769px]">
              <p>
                {t('empty.text')}
              </p>
            </div>

            <button
              onClick={handleCreateNew}
              disabled={!canCreateNew}
              className="relative inline-block"
            >
              <div className={`${canCreateNew
                ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                } rounded-[16px] px-[24px] py-[12px] transition-colors inline-block`}>
                <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                  {t('newButton')}
                </p>
              </div>
            </button>
          </div>
          </>
        ) : (
          /* List with Selected MVP Plan */
          <div className="flex flex-col lg:flex-row gap-[24px]">
            {/* Left Sidebar - MVP Plans List */}
            <div className="flex flex-col gap-[8px] w-full lg:w-[424px] lg:flex-shrink-0 lg:sticky lg:top-[24px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
              <PageHeader title={t('title')} description={pageDescription} />
              <AiItemsList
                items={mvpPlans}
                selectedId={selectedMvpPlan?.id ?? null}
                getItemTitle={getMvpPlanListTitle}
                getItemSubtitle={getMvpPlanSubtitle}
                onSelect={handleSelectMvpPlan}
              />

              {/* Create New Button */}
              <button
                onClick={handleCreateNew}
                disabled={!canCreateNew}
                onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-new-sidebar', type: 'create' })}
                onMouseLeave={() => setTooltipVisible(null)}
                className="relative"
              >
                <div className={`${canCreateNew
                  ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                  : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                  } rounded-[16px] px-[24px] py-[12px] transition-colors`}>
                  <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                    {t('newButton')}
                  </p>
                </div>

                {tooltipVisible?.id === 'create-new-sidebar' && !canCreateNew && (
                  <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                    <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                      {t('tooltip.paidOnly')}
                    </p>
                  </div>
                )}
              </button>

              {/* Upgrade banner for free tier */}
              {isFree && (
                <SubscriptionLimitBanner
                  title={t('paidBanner.title')}
                  description={t('upgradeBanner.description')}
                  buttonText={t('upgradeBanner.button')}
                />
              )}
            </div>

            {/* Right Content - Selected MVP Plan */}
            {selectedMvpPlan && (
              <AiDetailPanel
                key={selectedMvpPlan.id}
                title={getMvpPlanTitle(selectedMvpPlan)}
                content={selectedMvpPlan.generated_text}
                canEdit={canEdit}
                onEdit={() => handleEditMvpPlan(selectedMvpPlan)}
                editButtonText={t('detail.editButton')}
                tooltipEditText={t('tooltip.paidOnlyShort')}
                canDelete={canDelete}
                onDelete={() => {
                  setMvpPlanToDelete(selectedMvpPlan.id);
                  setShowDeleteConfirm(true);
                }}
                actionButtons={[
                  { text: t('detail.actionResearch'), onClick: () => navigate('/research') },
                  { text: t('detail.actionChat'), onClick: () => navigate('/chat') },
                ]}
                pdfExportData={selectedMvpPlan.generated_text ? {
                  title: getMvpPlanTitle(selectedMvpPlan),
                  content: selectedMvpPlan.generated_text,
                  artifactType: 'mvp_plan',
                  artifactTypeLabel: t('detail.pdfLabel'),
                  projectName: projects.find(p => p.id === selectedMvpPlan.project)?.name || t('defaultProjectName'),
                  date: selectedMvpPlan.created,
                } : undefined}
                tooltipDeleteText={t('tooltip.paidOnlyShort')}
                emptyText={t('detail.emptyText')}
                footer={selectedMvpPlan.generated_text ? <CrossSellCard target="service" placement="mvp_plan" /> : undefined}
              />
            )}
          </div>
        )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setMvpPlanToDelete(null);
        }}
        onConfirm={() => mvpPlanToDelete && handleDelete(mvpPlanToDelete)}
        title={t('confirm.deleteTitle')}
        description={t('confirm.deleteDescription')}
        confirmText={t('confirm.deleteConfirm')}
        cancelText={t('confirm.deleteCancel')}
      />

      {/* MVP Plan Creation Modal */}
      <MvpPlanCreationModal
        isOpen={showNewModal || showEditModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        projects={projects}
        marketAnalyses={marketAnalyses}
        positionings={positionings}
        personas={personas}
        businessModels={businessModels}
        initialData={formData}
        isEditing={isEditing}
        isLoading={isLoading}
      />
    </>
  );
}
